import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Trash2, MailOpen, Mail, Save } from "lucide-react";
import { api, fmtApiError } from "@/api";
import { usePageMeta } from "@/components/Reveal";
import { LogoMark } from "@/components/Header";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("gap_token", data.access_token);
      onSuccess(data.user);
    } catch (err) {
      setError(fmtApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-20" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl gold-frame bg-[#101A2E] p-10">
        <div className="flex justify-center mb-6"><LogoMark size={64} /></div>
        <h1 className="font-display text-2xl text-center text-[#F6F1E6] mb-2">Espace administration</h1>
        <p className="text-xs text-center text-[#6B7385] tracking-[0.2em] uppercase mb-8">Accès réservé</p>
        {error && <p className="text-xs text-red-400 mb-4 text-center" data-testid="admin-login-error">{error}</p>}
        <div className="space-y-4">
          <Input type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className="bg-[#0A0F1E] border-[#D4AF37]/20 text-[#F6F1E6] h-12" />
          <Input type="password" required placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className="bg-[#0A0F1E] border-[#D4AF37]/20 text-[#F6F1E6] h-12" />
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="gold-button w-full rounded-full px-6 py-3.5 text-xs font-medium tracking-[0.2em] uppercase disabled:opacity-50">
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PropertyEditor({ property, onSaved }) {
  const [form, setForm] = useState({
    ...property,
    amenities: (property.amenities || []).join("\n"),
    images: (property.images || []).join("\n"),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        amenities: form.amenities.split("\n").map((s) => s.trim()).filter(Boolean),
        images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.put(`/admin/properties/${property.key}`, payload);
      onSaved(data);
      toast.success(`« ${data.name} » mis à jour.`);
    } catch (err) {
      toast.error(fmtApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 text-[#F6F1E6]";
  const labelCls = "text-[10px] tracking-[0.2em] uppercase text-[#9AA3B5]";

  return (
    <div className="rounded-3xl border border-[#D4AF37]/20 bg-[#101A2E]/60 p-7 sm:p-9" data-testid={`admin-editor-${property.key}`}>
      <h3 className="font-display text-xl text-[#F6F1E6] mb-7">{property.name || property.key}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div><Label className={labelCls}>Nom du logement</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-name`} /></div>
        <div><Label className={labelCls}>Localisation</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-location`} /></div>
        <div><Label className={labelCls}>Capacité d'accueil</Label><Input value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-capacity`} placeholder="6 voyageurs · 3 chambres…" /></div>
        <div><Label className={labelCls}>Note (ex. Noté 5,0 / 5 sur Airbnb)</Label><Input value={form.rating} onChange={(e) => set("rating", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-rating`} /></div>
      </div>
      <div className="mt-5"><Label className={labelCls}>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-description`} /></div>
      <div className="mt-5"><Label className={labelCls}>Informations voyageurs</Label><Textarea rows={3} value={form.traveler_info} onChange={(e) => set("traveler_info", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-traveler-info`} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
        <div><Label className={labelCls}>Équipements (un par ligne)</Label><Textarea rows={5} value={form.amenities} onChange={(e) => set("amenities", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-amenities`} /></div>
        <div><Label className={labelCls}>Photos — URLs (une par ligne)</Label><Textarea rows={5} value={form.images} onChange={(e) => set("images", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-images`} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
        <div><Label className={labelCls}>Lien annonce Airbnb</Label><Input value={form.airbnb_url} onChange={(e) => set("airbnb_url", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-airbnb`} placeholder="https://www.airbnb.fr/rooms/…" /></div>
        <div>
          <Label className={labelCls}>Lien annonce Booking.com</Label>
          <Input value={form.booking_url} onChange={(e) => set("booking_url", e.target.value)} className={inputCls} data-testid={`admin-${property.key}-booking`} placeholder="Laissez vide si non disponible" />
          <p className="text-[10px] text-[#6B7385] mt-1.5">Si vide, le bouton Booking.com n'apparaît pas sur le site.</p>
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid={`admin-${property.key}-save`} className="gold-button rounded-full px-8 py-3 text-[11px] font-medium tracking-[0.18em] uppercase mt-7 flex items-center gap-2 disabled:opacity-50">
        <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}

function Messages() {
  const [messages, setMessages] = useState(null);

  const load = () => api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => setMessages([]));
  useEffect(() => { load(); }, []);

  const toggleRead = async (id) => { await api.patch(`/admin/messages/${id}/read`); load(); };
  const remove = async (id) => { await api.delete(`/admin/messages/${id}`); toast.success("Message supprimé."); load(); };

  if (messages === null) return <p className="text-sm text-[#9AA3B5] py-8 text-center">Chargement…</p>;
  if (messages.length === 0) return <p className="text-sm text-[#9AA3B5] py-8 text-center" data-testid="messages-empty">Aucune demande reçue pour le moment.</p>;

  return (
    <div className="space-y-4" data-testid="admin-messages-list">
      {messages.map((m) => (
        <div key={m.id} className={`rounded-2xl border p-6 ${m.read ? "border-[#D4AF37]/10 bg-[#101A2E]/40 opacity-70" : "border-[#D4AF37]/30 bg-[#101A2E]"}`} data-testid={`message-${m.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-sm text-[#F6F1E6] font-medium">{m.name} <span className="text-[#6B7385] font-normal">· <a href={`mailto:${m.email}`} className="text-[#E5C158]">{m.email}</a>{m.phone ? ` · ${m.phone}` : ""}</span></p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] mt-1">{m.subject} · {new Date(m.created_at).toLocaleString("fr-FR")}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggleRead(m.id)} data-testid={`message-read-${m.id}`} className="p-2 rounded-full border border-[#D4AF37]/25 text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors" title={m.read ? "Marquer non lu" : "Marquer lu"}>
                {m.read ? <Mail size={15} /> : <MailOpen size={15} />}
              </button>
              <button onClick={() => remove(m.id)} data-testid={`message-delete-${m.id}`} className="p-2 rounded-full border border-red-500/25 text-red-400 hover:bg-red-500/10 transition-colors" title="Supprimer">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
          <p className="text-sm text-[#B9BFD0] leading-relaxed whitespace-pre-wrap">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);
  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 text-[#F6F1E6]";
  const labelCls = "text-[10px] tracking-[0.2em] uppercase text-[#9AA3B5]";

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Coordonnées mises à jour.");
    } catch (err) {
      toast.error(fmtApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl border border-[#D4AF37]/20 bg-[#101A2E]/60 p-7 sm:p-9 max-w-2xl" data-testid="admin-settings">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div><Label className={labelCls}>E-mail de contact</Label><Input value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} data-testid="settings-email" /></div>
        <div><Label className={labelCls}>Téléphone</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="settings-phone" placeholder="+33 6 12 34 56 78" /></div>
        <div><Label className={labelCls}>WhatsApp (format international, ex. +33612345678)</Label><Input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} data-testid="settings-whatsapp" /></div>
        <div><Label className={labelCls}>Zone d'intervention</Label><Input value={form.zone || ""} onChange={(e) => set("zone", e.target.value)} className={inputCls} data-testid="settings-zone" /></div>
        <div><Label className={labelCls}>URL Instagram</Label><Input value={form.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} className={inputCls} data-testid="settings-instagram" /></div>
        <div><Label className={labelCls}>URL Facebook</Label><Input value={form.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} className={inputCls} data-testid="settings-facebook" placeholder="https://www.facebook.com/…" /></div>
      </div>
      <button onClick={save} disabled={saving} data-testid="settings-save" className="gold-button rounded-full px-8 py-3 text-[11px] font-medium tracking-[0.18em] uppercase mt-7 flex items-center gap-2 disabled:opacity-50">
        <Save size={14} /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}

function Security() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Mot de passe modifié avec succès.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(fmtApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-3xl border border-[#D4AF37]/20 bg-[#101A2E]/60 p-7 sm:p-9 max-w-md space-y-5" data-testid="admin-security">
      <div><Label className="text-[10px] tracking-[0.2em] uppercase text-[#9AA3B5]">Mot de passe actuel</Label><Input type="password" required value={form.current_password} onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))} className="mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 text-[#F6F1E6]" data-testid="security-current" /></div>
      <div><Label className="text-[10px] tracking-[0.2em] uppercase text-[#9AA3B5]">Nouveau mot de passe (8 caractères min.)</Label><Input type="password" required minLength={8} value={form.new_password} onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))} className="mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 text-[#F6F1E6]" data-testid="security-new" /></div>
      <div><Label className="text-[10px] tracking-[0.2em] uppercase text-[#9AA3B5]">Confirmer le nouveau mot de passe</Label><Input type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} className="mt-2 bg-[#0A0F1E] border-[#D4AF37]/20 text-[#F6F1E6]" data-testid="security-confirm" /></div>
      <button type="submit" disabled={saving} data-testid="security-save" className="gold-button rounded-full px-8 py-3 text-[11px] font-medium tracking-[0.18em] uppercase disabled:opacity-50">
        {saving ? "Modification…" : "Changer le mot de passe"}
      </button>
    </form>
  );
}

export default function Admin() {
  usePageMeta("Administration", "Espace d'administration Golden Aura Pro.");
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    if (!localStorage.getItem("gap_token")) { setReady(true); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("gap_token"))
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (user) api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, [user]);

  if (!ready) return <div className="py-40 text-center text-[#9AA3B5]">Chargement…</div>;
  if (!user) return <Login onSuccess={setUser} />;

  const logout = () => {
    localStorage.removeItem("gap_token");
    setUser(null);
    toast.success("Vous êtes déconnecté.");
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
        <div>
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-2">Espace administration</p>
          <h1 className="font-display font-light text-3xl sm:text-4xl text-[#F6F1E6]">Golden Aura Pro</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn" className="outline-button rounded-full px-6 py-2.5 text-[11px] font-medium tracking-[0.16em] uppercase flex items-center gap-2">
          <LogOut size={14} /> Déconnexion
        </button>
      </div>

      <Tabs defaultValue="logements">
        <TabsList className="bg-[#101A2E] border border-[#D4AF37]/20 mb-8" data-testid="admin-tabs">
          <TabsTrigger value="logements" data-testid="tab-logements">Logements</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Coordonnées</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Sécurité</TabsTrigger>
        </TabsList>
        <TabsContent value="logements" className="space-y-8">
          {properties.map((p) => (
            <PropertyEditor key={p.key} property={p} onSaved={(updated) => setProperties((ps) => ps.map((x) => (x.key === updated.key ? updated : x)))} />
          ))}
        </TabsContent>
        <TabsContent value="messages"><Messages /></TabsContent>
        <TabsContent value="settings"><Settings /></TabsContent>
        <TabsContent value="security"><Security /></TabsContent>
      </Tabs>
    </div>
  );
}
